import React, { useEffect, useRef, useState } from "react";
import { clearSession } from "../../shared/_helpers/session";
import { logOut } from "../../shared/_services/auth.services";
import { useNavigate } from "react-router-dom";
import ReactSelect, { components } from "react-select";
import { useSnackbar } from "notistack";
import StarsIcon from "../../styles/StarsIcon";
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Field,
    Input,
    Page,
    PageHead,
    Panel,
    Select,
    Textarea,
    cn,
} from "../ui";
import Notifications from "../Notifications";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { checkBlockedStatus, getAllUserList, getDahboardData, getNotifications, markNotificationSeen, sendManualNotification } from "../../shared/_services/common.services";
import Loader from "../Loader";

const FILTER_PERIODS = [
    { label: "Today", value: "toDay", href: "today" },
    { label: "This Week", value: "thisWeek", href: "this week" },
    { label: "This Month", value: "thisMonth", href: "this month" },
];

const NOTIFICATION_MODE_OPTIONS = [
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
    { value: "web", label: "Web Notification" },
    { value: "push", label: "Push Notification" },
];

const CLIENT_TYPE_OPTIONS = [
    { value: "all", label: "All Clients" },
    { value: "not_onboarded", label: "Not Onboarded Clients" },
    { value: "selected", label: "Select Clients From List" },
];

const NOTIFICATION_INITIAL_VALUES = {
    mode: [],
    filter: "",
    userIds: [],
    title: "",
    body: "",
};

const NOTIFICATION_VALIDATION_SCHEMA = Yup.object().shape({
    mode: Yup.array().min(1, "Select at least one mode").required("Select at least one mode"),
    filter: Yup.string().required("Select a client type"),
    userIds: Yup.array().when("filter", {
        is: "selected",
        then: (schema) => schema.min(1, "Select at least one client").required("Select at least one client"),
        otherwise: (schema) => schema.optional(),
    }),
    title: Yup.string().trim().required("Title is required"),
    body: Yup.string().trim().required("Message is required"),
});

function getUpdatedNotificationModes(currentModes, modeValue, isChecked) {
    return isChecked
        ? [...currentModes, modeValue]
        : currentModes.filter((mode) => mode !== modeValue);
}

function getSelectedClientTypeValue(selected) {
    return selected ? selected.value : "";
}

function shouldResetUserIds(selected) {
    return selected?.value !== "selected";
}

function handleClientSearchInputChange(value, meta, setClientSearch) {
    if (meta.action === "input-change") setClientSearch(value);
    if (meta.action === "menu-close") setClientSearch("");
    return value;
}

function isClientOptionMatch(option, input, clientSearch) {
    const query = input || clientSearch;
    if (!query) return true;
    return option?.data?.searchText?.includes(query.toLowerCase());
}

function mapSelectedOptionsToIds(selected) {
    return selected ? selected.map((option) => option.value) : [];
}

function redirectSessionExpired(navigate, enqueueSnackbar) {
    clearSession();
    enqueueSnackbar("Your session has been expired!", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
    });
    navigate("/login");
}

function isUnauthorizedResponse(response) {
    return response?.response?.status === 401;
}

function applyClientListResponse(response, setClientOptions, navigate, enqueueSnackbar) {
    if (response.status === 200 && Array.isArray(response.data)) {
        setClientOptions(response.data.map(mapClientToSelectOption));
        return;
    }
    if (isUnauthorizedResponse(response)) {
        redirectSessionExpired(navigate, enqueueSnackbar);
    }
}

function applyDashboardDataResponse(data, setDashboardData, navigate, enqueueSnackbar) {
    if (data.status === 200) {
        setDashboardData({ ...data?.data });
        return;
    }
    if (isUnauthorizedResponse(data)) {
        redirectSessionExpired(navigate, enqueueSnackbar);
    }
}

function applyNotificationListResponse(data, setNotification, setNewNotification, enqueueSnackbar) {
    if (data.status === 200) {
        const unseenNotifications = data?.data?.data?.filter((notification) => !notification.seen);
        setNotification(unseenNotifications);
        if (unseenNotifications.length > 0) {
            setNewNotification(true);
        }
        return;
    }
    if (isUnauthorizedResponse(data)) {
        clearSession();
        enqueueSnackbar("No New Notifications", {
            variant: "success",
            anchorOrigin: { horizontal: "right", vertical: "top" },
        });
    }
}

async function submitManualNotificationForm(values, { resetForm, setSubmitting }, navigate, enqueueSnackbar) {
    const payload = {
        mode: values.mode,
        filter: values.filter,
        userIds: values.filter === "selected" ? values.userIds : [],
        title: values.title.trim(),
        body: values.body.trim(),
    };
    try {
        const response = await sendManualNotification(payload);
        if (response.status === 200) {
            enqueueSnackbar("Notification sent successfully", { variant: "success" });
            resetForm();
        } else if (isUnauthorizedResponse(response)) {
            redirectSessionExpired(navigate, enqueueSnackbar);
        } else {
            enqueueSnackbar("Failed to send notification", { variant: "error" });
        }
    } catch (error) {
        enqueueSnackbar("Failed to send notification", { variant: "error" });
    } finally {
        setSubmitting(false);
    }
}

function mapClientToSelectOption(client) {
    const name = [client.firstName, client.lastName].filter(Boolean).join(" ").trim();
    const email = client.email || "";
    const combined = name ? `${name} (${email})` : email;
    return {
        value: client._id,
        label: combined,
        searchText: `${name} ${email}`.toLowerCase(),
        title: combined,
    };
}

async function runBlockedStatusCheck(userId, navigate) {
    try {
        const response = await checkBlockedStatus(userId);

        if (response.data ? response.data.status : response.response.status === 401) {
            clearSession();
            navigate("/login");
        } else {
            const { isBlocked, isDeleted } = response.data;

            if (isBlocked || isDeleted) {
                clearSession();
                navigate("/login");
            }
        }
    } catch (error) {
        console.error("Error checking blocked status:", error);
    }
}

function StatCardWithFilter({
    icon,
    value,
    title,
    filterKey,
    selectedFilter,
    setSelectedFilter,
}) {
    const current = selectedFilter[filterKey];
    return (
        <Panel as="div" className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-start tw-justify-between">
                <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-tile tw-bg-accent-tint tw-text-text-accent">
                    <StarsIcon name={icon} className="tw-h-5 tw-w-5 tw-shrink-0" />
                </span>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="icon" className="tw-border-solid" size="sm" iconOnly aria-label={`Filter ${title}`}>
                            <StarsIcon name="filter" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {FILTER_PERIODS.map((period) => (
                            <DropdownMenuItem
                                key={period.value}
                                aria-current={current === period.value ? "true" : undefined}
                                className={current === period.value ? "tw-bg-surface-selected" : undefined}
                                onSelect={() => {
                                    setSelectedFilter({ ...selectedFilter, [filterKey]: period.value });
                                }}
                            >
                                {period.label}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <p className="tw-m-0 tw-text-2xl tw-font-bold tw-tabular-nums tw-text-text">{value}</p>
            <p className="tw-m-0 tw-text-sm tw-text-text-muted">{title}</p>
        </Panel>
    );
}

// R425: react-select unstyled, styled through classNames. These strings live outside
// className=, so the post-beta prefix switch must rename them.
const clientSelectClassNames = {
    control: ({ isFocused }) =>
        cn(
            "tw-min-h-12 tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-surface tw-px-3 tw-text-md",
            isFocused && "tw-outline tw-outline-2 tw-outline-offset-1 tw-outline-focus-ring",
        ),
    valueContainer: () => "tw-gap-1",
    placeholder: () => "tw-text-text-placeholder",
    menu: () =>
        "tw-mt-1 tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-divider tw-bg-surface tw-shadow-3 tw-p-1",
    option: ({ isFocused, isSelected }) =>
        cn(
            "tw-cursor-pointer tw-rounded-md tw-px-3 tw-py-2 tw-text-sm",
            isFocused && "tw-bg-surface-sunken",
            isSelected && "tw-bg-surface-selected tw-font-semibold",
        ),
    multiValue: () => "tw-mr-1 tw-rounded-pill tw-bg-surface-selected tw-px-2 tw-text-xs tw-font-bold",
    multiValueRemove: () => "tw-ml-1 hover:tw-text-danger",
    indicatorsContainer: () => "tw-gap-1 tw-text-text-muted",
};

const CheckboxOption = (props) => {
    const fullLabel = props?.data?.title || props.label;
    const displayLabel = fullLabel && fullLabel.length > 40 ? `${fullLabel.slice(0, 37)}...` : fullLabel;

    return (
        <components.Option {...props}>
            <span className="tw-flex tw-items-center tw-gap-2">
                <Checkbox
                    checked={props.isSelected}
                    onChange={() => null}
                    tabIndex={-1}
                />
                <span title={fullLabel}>{displayLabel}</span>
            </span>
        </components.Option>
    );
};

const ValueContainer = ({ children, ...props }) => {
    const selected = props.getValue();
    const count = selected.length;

    return (
        <components.ValueContainer {...props}>
            {count > 0
                ? `${count} Clients Selected`
                : props.selectProps.placeholder}
            <div className="tw-hidden">{children}</div>
        </components.ValueContainer>
    );
};

const ClientMenuList = (props) => {
    const { menuSearchValue, onMenuSearchChange } = props.selectProps;
    return (
        <components.MenuList {...props}>
            <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-surface tw-p-2">
                <Input
                    type="text"
                    className="tw-min-h-10"
                    placeholder="Search clients..."
                    value={menuSearchValue}
                    onChange={(e) => onMenuSearchChange(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
            {props.children}
        </components.MenuList>
    );
};

function ManualNotificationForm({
    onSubmit,
    clientOptions,
    clientSelectRef,
    clientMenuOpen,
    setClientMenuOpen,
    clientSearch,
    setClientSearch,
}) {
    return (
        <Formik
            enableReinitialize
            initialValues={NOTIFICATION_INITIAL_VALUES}
            validationSchema={NOTIFICATION_VALIDATION_SCHEMA}
            onSubmit={onSubmit}
        >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                <Form>
                    <div className="tw-grid tw-gap-x-8 md:tw-grid-cols-2">
                        <div>
                            <fieldset className="tw-m-0 tw-mb-4 tw-min-w-0 tw-border-0 tw-p-0">
                                <legend className="tw-mb-2 tw-p-0 tw-text-sm tw-font-semibold tw-text-text">Select Notification Mode</legend>
                                <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2">
                                    {NOTIFICATION_MODE_OPTIONS.map((mode) => (
                                        <Checkbox
                                            key={mode.value}
                                            id={mode.value}
                                            label={mode.label}
                                            checked={values.mode.includes(mode.value)}
                                            onChange={(e) => {
                                                const updated = getUpdatedNotificationModes(values.mode, mode.value, e.target.checked);
                                                setFieldValue("mode", updated);
                                            }}
                                        />
                                    ))}
                                </div>
                                {touched.mode && errors.mode && <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-danger">{errors.mode}</p>}
                            </fieldset>

                            <Field
                                label="Select Client Type"
                                id="client-type"
                                error={touched.filter && errors.filter ? errors.filter : undefined}
                                className="md:tw-w-1/2"
                            >
                                <Select
                                    value={values.filter}
                                    onChange={(e) => {
                                        const selected = CLIENT_TYPE_OPTIONS.find((opt) => opt.value === e.target.value) || null;
                                        setFieldValue("filter", getSelectedClientTypeValue(selected));
                                        if (shouldResetUserIds(selected)) {
                                            setFieldValue("userIds", []);
                                        }
                                    }}
                                >
                                    <option value="">Select...</option>
                                    {CLIENT_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Select>
                            </Field>

                            {values.filter === "selected" && (
                                <div className="tw-mb-4" ref={clientSelectRef}>
                                    <label className="tw-mb-2 tw-block tw-text-sm tw-font-semibold tw-text-text">Select Clients</label>
                                    <ReactSelect
                                        unstyled
                                        classNames={clientSelectClassNames}
                                        isMulti
                                        isSearchable
                                        options={clientOptions}
                                        placeholder="Select..."
                                        openMenuOnClick
                                        menuIsOpen={clientMenuOpen}
                                        onMenuOpen={() => setClientMenuOpen(true)}
                                        onMenuClose={() => {
                                            setClientMenuOpen(false);
                                            setClientSearch("");
                                        }}
                                        onFocus={() => setClientMenuOpen(true)}
                                        onInputClick={() => setClientMenuOpen((prev) => !prev)}
                                        inputValue={clientSearch}
                                        onInputChange={(val, meta) => {
                                            return handleClientSearchInputChange(val, meta, setClientSearch);
                                        }}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        menuPlacement="top"
                                        value={clientOptions.filter((opt) => values.userIds.includes(opt.value))}
                                        filterOption={(option, input) => {
                                            return isClientOptionMatch(option, input, clientSearch);
                                        }}
                                        onChange={(selected) => {
                                            const ids = mapSelectedOptionsToIds(selected);
                                            setFieldValue("userIds", ids);
                                        }}
                                        components={{
                                            Option: CheckboxOption,
                                            ValueContainer: ValueContainer,
                                            MenuList: ClientMenuList,
                                        }}
                                        menuSearchValue={clientSearch}
                                        onMenuSearchChange={setClientSearch}
                                    />
                                    {touched.userIds && errors.userIds && <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-danger">{errors.userIds}</p>}
                                </div>
                            )}
                        </div>
                        <div>
                            <Field
                                label="Title"
                                id="title"
                                error={touched.title && errors.title ? errors.title : undefined}
                            >
                                <Input
                                    type="text"
                                    placeholder="Enter title"
                                    value={values.title}
                                    onChange={(e) => setFieldValue("title", e.target.value)}
                                />
                            </Field>
                            <Field
                                label="Message"
                                id="message"
                                error={touched.body && errors.body ? errors.body : undefined}
                            >
                                <Textarea
                                    placeholder="Enter message"
                                    value={values.body}
                                    onChange={(e) => setFieldValue("body", e.target.value)}
                                />
                            </Field>
                            <div className="tw-flex">
                                <Button type="submit" disabled={isSubmitting}>
                                    <StarsIcon name="send" className="tw-h-4 tw-w-4 tw-shrink-0" />
                                    {isSubmitting ? "Sending..." : "Send"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

function AdminDashboard() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [confirmLogoutPopupShow, setConfirmLogoutPopupShow] = useState(false);
    const confirmLogoutPopupClose = () => setConfirmLogoutPopupShow(false);
    const [notificationPopupShowAdmin, setNotificationPopupShowAdmin] = useState(false);
    const [dashboardData, setDashboardData] = useState({});
    const [showAdminLoader, setShowAdminLoader] = useState(false);
    const [clientMenuOpen, setClientMenuOpen] = useState(false);
    const clientSelectRef = useRef(null);
    const [clientSearch, setClientSearch] = useState("");
    const [selectedFilter, setSelectedFilter] = useState({
        "totalClients": "thisMonth",
        "totalActiveClients": "thisMonth",
        "totalNumberOfMeetings": "thisMonth",
        "averageClientSatisfactionRating": "thisMonth"
    });
    const [notification, setNotification] = useState([]);
    const [newNotificaiton, setNewNotification] = useState(false);
    const [clientOptions, setClientOptions] = useState([]);

    const loadClients = async () => {
        const response = await getAllUserList("client");
        applyClientListResponse(response, setClientOptions, navigate, enqueueSnackbar);
    };

    const getAdminDashboardData = async () => {
        setShowAdminLoader(true);
        const data = await getDahboardData({ filter: selectedFilter });
        applyDashboardDataResponse(data, setDashboardData, navigate, enqueueSnackbar);
        setShowAdminLoader(false);
    };

    const checkBlocked = async () => {
        const userId = JSON.parse(localStorage.getItem("currentUser")).user?._id;
        await runBlockedStatusCheck(userId, navigate);
    };

    const getNotificationData = async () => {
        const currentUser = JSON.parse(localStorage.getItem("currentUser"));
        const userId = currentUser?.user?._id;
        const data = await getNotifications(userId);
        applyNotificationListResponse(data, setNotification, setNewNotification, enqueueSnackbar);
    };

    const handleManualNotificationSubmit = (values, helpers) =>
        submitManualNotificationForm(values, helpers, navigate, enqueueSnackbar);

    useEffect(() => {
        checkBlocked();
        getNotificationData();
        getAdminDashboardData();
    }, [selectedFilter]); //eslint-disable-line

    useEffect(() => {
        loadClients();
    }, []); //eslint-disable-line

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (clientSelectRef.current && !clientSelectRef.current.contains(event.target)) {
                setClientMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const openConfirmLogoutPopup = () => {
        setConfirmLogoutPopupShow(true);
    };

    const handleLogout = async () => {
      // logOut clears the stored session itself, whatever the server answered,
      // so this always navigates. Gating the clear on a 200 is what made logout
      // unusable: the request had the wrong shape for SEC-115 (#72), so the
      // branch never ran and the only feedback was an error toast. Even with the
      // right shape a 15-minute access token can expire in an open tab, and a
      // logout that refuses to work because the server said 401 is a worse
      // outcome than an orphaned refresh token nobody holds a copy of.
      const data = await logOut();

      if (data?.status !== 200) {
        enqueueSnackbar("Signed out on this device, but the server could not end the session.", {
          variant: "warning",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      }

      navigate("/login");
    };

    const handleNotificationsClick = async () => {
        setNotificationPopupShowAdmin(true);
        setNewNotification(false)

        for (const notif of notification) {
            try {
                await markNotificationSeen(notif._id); // Call API for each unseen notification

            } catch (error) {
                console.error(`Error marking notification ${notif._id} as seen:`, error);
            }
        }
    };

    const handleNotificationCloseAdmin = (state) => {
        setNotificationPopupShowAdmin(state);
    }

    return (
        <Page>
            <PageHead
                title="Dashboard"
                tools={
                    <>
                        <Button
                            variant="icon"
                            iconOnly
                            className="tw-border-solid tw-relative"
                            onClick={handleNotificationsClick}
                            title="Notifications"
                            aria-label="Notifications"
                            data-slot="circle-button"
                            data-new-notification={newNotificaiton ? "true" : "false"}
                        >
                            <StarsIcon name="bell" className="tw-h-5 tw-w-5 tw-shrink-0" />
                            {newNotificaiton && (
                                <span aria-hidden="true" className="tw-absolute tw-right-2 tw-top-2 tw-h-2 tw-w-2 tw-rounded-pill tw-bg-text-accent" />
                            )}
                        </Button>
                        <Notifications onClose={handleNotificationCloseAdmin} showModal={notificationPopupShowAdmin} />
                        <Button
                            variant="icon"
                            className="tw-border-solid"
                            iconOnly
                            onClick={openConfirmLogoutPopup}
                            title="Log Out"
                            aria-label="Log Out"
                            data-slot="circle-button"
                        >
                            <StarsIcon name="logout" className="tw-h-5 tw-w-5 tw-shrink-0" />
                        </Button>
                    </>
                }
            />

            {showAdminLoader ? <Loader /> : <div data-slot="admin-dashboard" className="tw-grid tw-gap-6">
                <div className="tw-grid tw-gap-6 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
                    <StatCardWithFilter
                        icon="users"
                        value={dashboardData?.totalClients || 0}
                        title="Total Registered Clients"
                        filterKey="totalClients"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                    <StatCardWithFilter
                        icon="check"
                        value={dashboardData?.totalActiveClients || 0}
                        title="Total Active Clients"
                        filterKey="totalActiveClients"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                    <StatCardWithFilter
                        icon="calendar"
                        value={dashboardData?.totalNumberOfMeetings || 0}
                        title="Total No. of Meetings"
                        filterKey="totalNumberOfMeetings"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                    <StatCardWithFilter
                        icon="chart"
                        value={dashboardData?.averageClientSatisfactionRating ? dashboardData?.averageClientSatisfactionRating : "0"}
                        title="Average Client Satisfaction Rating"
                        filterKey="averageClientSatisfactionRating"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                </div>
                <Panel title="Send Manual Notification">
                    <ManualNotificationForm
                        onSubmit={handleManualNotificationSubmit}
                        clientOptions={clientOptions}
                        clientSelectRef={clientSelectRef}
                        clientMenuOpen={clientMenuOpen}
                        setClientMenuOpen={setClientMenuOpen}
                        clientSearch={clientSearch}
                        setClientSearch={setClientSearch}
                    />
                </Panel>
            </div>}

            {/* confirm logout popup */}
            <Dialog
                open={confirmLogoutPopupShow}
                onOpenChange={(open) => { if (!open) confirmLogoutPopupClose(); }}
            >
                <DialogContent
                    onInteractOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    className="tw-text-center"
                >
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <span />
                        <Button
                            variant="icon"
                            size="sm"
                            iconOnly
                            aria-label="Close"
                            className="tw-border-solid tw-shrink-0"
                            onClick={confirmLogoutPopupClose}
                        >
                            <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        </Button>
                    </div>
                    <p className="tw-m-0 tw-mb-2 tw-text-md tw-text-text-muted">You are attempting to logout of Astra.</p>
                    <DialogTitle>Are you sure?</DialogTitle>
                    <div className="tw-mt-6 tw-flex tw-justify-center">
                        <Button onClick={handleLogout}>
                            <StarsIcon name="check" className="tw-h-4 tw-w-4 tw-shrink-0" />
                            Logout
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Page>

    );
}

export default AdminDashboard;
